import { useMemo } from 'react'

import useSWR from 'swr'

import { getCloudRegions, getGlobalClusters } from '~/api'
import { useUserStore } from '~/hooks/state'
import { useQueryParams } from '~/hooks/use-query-params'
import envConfig, { getLocalStorageItem } from '~/utils/env-config'

export const useOverviewTableQuery = () => {
  const isInternalUser = useUserStore((state) => state.user?.internal)
  const showDeletedClusters = useMemo(() => {
    const localStorageVal = getLocalStorageItem('search.show_deleted_clusters')
    if (localStorageVal !== null) {
      return localStorageVal === 'true'
    }

    // prod: not show deleted clusters default
    // others: show deleted clusters default
    return envConfig.appReleaseEnv !== 'prod'
  }, [])

  const { queryParams, setQueryParams } = useQueryParams({
    page: 1,
    pageSize: 10,
    query: '',
    clusterStatus: [],
    showDeleted: showDeletedClusters,
    tenantPlan: [],
    controlPlaneArch: [],
    deployType: [],
    region: [],
    sort: '',
    order: ''
  })
  const { page, pageSize, query } = queryParams
  const search = (val: string) => {
    setQueryParams({ page: 1, query: val.trim() })
  }
  const {
    data: resp,
    error,
    isValidating,
    mutate
  } = useSWR(['/api/v1/dashboard/clusters', queryParams], () =>
    getGlobalClusters(
      queryParams.query,
      queryParams.showDeleted,
      queryParams.clusterStatus,
      queryParams.clusterVersion,
      queryParams.tenantPlan,
      queryParams.controlPlaneArch,
      queryParams.provider,
      queryParams.region,
      isInternalUser ? queryParams.deployType : undefined,
      queryParams.sort,
      queryParams.order,
      queryParams.pageSize,
      queryParams.page
    )
  )
  const { data: regionData } = useSWR(['/api/v1/dashboard/regions'], () =>
    getCloudRegions()
  )
  const regionFilters = useMemo(
    () =>
      regionData?.data?.items?.map((item) => ({
        text: item,
        value: item
      })) || [],
    [regionData]
  )
  const data = useMemo(() => resp?.data?.items || [], [resp])
  const total = resp?.data?.total || 0
  const loading = (!resp && !error) || (!regionData && !error)

  return {
    data,
    regionFilters,
    total,
    page,
    pageSize,
    queryParams,
    setQueryParams,
    loading,
    isValidating,
    search,
    query,
    refresh: mutate
  }
}
